import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Lock, Gem, ChevronRight, BookOpen } from "lucide-react";
import { AudioOutputPanel } from "@/components/projects/AudioOutputPanel";

const MODE_LABELS = {
  single_narrator: "Single Narrator",
  hybrid: "Hybrid",
  full_cast: "Full Cast",
};

function Param({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-slate-950/40 px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-sm text-slate-200">{value}</div>
    </div>
  );
}

const PIPELINE = [
  { label: "Manuscript", phase: "Phase 2", active: true, path: "manuscript" },
  { label: "Characters", phase: "Phase 3", active: true, path: "characters" },
  { label: "Pronunciations", phase: "Phase 4", active: true, path: "pronunciations" },
  { label: "Production", phase: "Phase 5", active: true, path: "production" },
  { label: "Review & Repair", phase: "Phase 6", active: true, path: "production" },
  { label: "Assembly & Export", phase: "Phase 7", active: true, path: "assembly" },
  { label: "Studio Sync", phase: "Phase 8", active: true, path: "studio" },
];

export function ProjectDetailView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/projects/${projectId}`)
      .then((r) => setProject(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load project"));
  }, [projectId]);

  if (error) {
    return (
      <div>
        <Button variant="ghost" onClick={() => navigate("/studio/projects")} className="mb-4 gap-2 text-slate-400">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  const preset = project.narrator_preset || {};

  return (
    <div>
      <Button
        variant="ghost"
        onClick={() => navigate("/studio/projects")}
        data-testid="back-to-projects"
        className="mb-4 gap-2 text-slate-400 hover:text-amber-200"
      >
        <ArrowLeft className="h-4 w-4" /> Productions
      </Button>

      <div className="mb-8 flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10 ring-1 ring-amber-500/25">
          <Gem className="h-6 w-6 text-amber-400" />
        </div>
        <div>
          <h1 data-testid="project-detail-title" className="font-serif text-4xl tracking-tight text-slate-100">
            {project.title}
          </h1>
          <p className="mt-1 text-sm text-slate-400">{project.author || "Unknown author"}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge className="rounded-full border-0 bg-amber-500/15 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-300 ring-1 ring-amber-500/30">
              {project.status.replace(/_/g, " ")}
            </Badge>
            <Badge className="rounded-full border-border bg-slate-900/50 px-2.5 py-0.5 font-mono text-[10px] text-slate-400" variant="outline">
              {MODE_LABELS[project.production_mode]}
            </Badge>
          </div>
        </div>
      </div>

      {/* Locked narrator preset */}
      <div className="mb-8 rounded-xl border border-amber-500/20 bg-[#12161f] p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl text-slate-100">Narrator preset</h2>
            <p className="text-sm text-slate-400">{preset.name}</p>
          </div>
          <Badge
            data-testid="preset-locked-badge"
            className="gap-1 rounded-full border-0 bg-slate-800/70 px-3 py-1 text-[10px] uppercase tracking-wider text-amber-300 ring-1 ring-amber-500/30"
          >
            <Lock className="h-3 w-3" /> Locked
          </Badge>
        </div>

        <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] px-4 py-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-500/70">ElevenLabs Voice ID</div>
          <div data-testid="narrator-voice-id" className="mt-1 select-all font-mono text-sm text-amber-200">
            {preset.voice_id}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Param label="Model" value={preset.model} />
          <Param label="Speed" value={preset.speed?.toFixed(1)} />
          <Param label="Stability" value={`${Math.round((preset.stability || 0) * 100)}%`} />
          <Param label="Similarity" value={`${Math.round((preset.similarity || 0) * 100)}%`} />
          <Param label="Style" value={`${Math.round((preset.style || 0) * 100)}%`} />
          <Param label="Speaker Boost" value={preset.speaker_boost ? "On" : "Off"} />
          <Param label="Language Override" value={preset.language_override ? "On" : "Off"} />
          <Param label="Review Output" value={preset.output} />
        </div>
        <p className="mt-4 text-xs text-slate-500">
          These settings are fixed for the House Diamond pilot and recorded on every generated take. Passage-level
          overrides become available in Phase 5.
        </p>
      </div>

      {/* Project Settings → Audio Output (ElevenLabs source-generation profiles) */}
      <AudioOutputPanel projectId={projectId} />

      {/* Production pipeline preview */}
      <div className="rounded-xl border border-border bg-[#12161f] p-6">
        <h2 className="mb-4 font-serif text-2xl text-slate-100">Production pipeline</h2>
        <div className="space-y-2">
          {PIPELINE.map((s) =>
            s.active ? (
              <button
                key={s.label}
                data-testid={`open-${s.path}-row`}
                onClick={() => navigate(`/studio/projects/${projectId}/${s.path}`)}
                className="flex w-full items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-left transition-colors hover:bg-amber-500/[0.12]"
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="h-4 w-4 text-amber-400" />
                  <span className="text-sm text-amber-100">{s.label}</span>
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-500/30">
                    Available
                  </span>
                </div>
                <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-amber-300">
                  Open <ChevronRight className="h-3 w-3" />
                </span>
              </button>
            ) : (
              <div
                key={s.label}
                className="flex items-center justify-between rounded-lg border border-border bg-slate-950/30 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <Lock className="h-4 w-4 text-slate-600" />
                  <span className="text-sm text-slate-300">{s.label}</span>
                </div>
                <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  {s.phase} <ChevronRight className="h-3 w-3" />
                </span>
              </div>
            )
          )}
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Each stage unlocks after the preceding phase is demonstrated and approved.
        </p>
      </div>
    </div>
  );
}
